import { useState, useEffect, useMemo, useCallback } from 'react';

// ==================== useTableSort ====================
// Client-side sorting with localStorage persistence
export function useTableSort(storageKey, defaultField = 'created_at', defaultDir = 'desc') {
  const [sortField, setSortField] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(`${storageKey}_sort`);
      if (saved) {
        try {
          return JSON.parse(saved).field || defaultField;
        } catch { }
      }
    }
    return defaultField;
  });

  const [sortDir, setSortDir] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(`${storageKey}_sort`);
      if (saved) {
        try {
          return JSON.parse(saved).dir || defaultDir;
        } catch { }
      }
    }
    return defaultDir;
  });

  // Persist to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(`${storageKey}_sort`, JSON.stringify({ field: sortField, dir: sortDir }));
    }
  }, [storageKey, sortField, sortDir]);

  const toggleSort = useCallback((field) => {
    if (field === sortField) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  }, [sortField]);

  const sortData = useCallback((data) => {
    if (!data || !Array.isArray(data)) return [];
    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle nested properties like 'branch_name'
      if (aVal === undefined && sortField.includes('_')) {
        aVal = a[sortField] || '';
      }
      if (bVal === undefined && sortField.includes('_')) {
        bVal = b[sortField] || '';
      }
      
      // Null/undefined handling
      aVal = aVal ?? '';
      bVal = bVal ?? '';
      
      // String comparison
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [sortField, sortDir]);

  return {
    sortField,
    sortDir,
    toggleSort,
    sortData,
    setSortField,
    setSortDir,
  };
}

// ==================== useSavedFilters ====================
// Save/load filter presets with localStorage
export function useSavedFilters(storageKey) {
  const SAVED_KEY = `${storageKey}_saved_filters`;
  const CURRENT_KEY = `${storageKey}_current_filters`;

  const [savedFilters, setSavedFilters] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        return JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
      } catch { }
    }
    return [];
  });

  // Load current filter state on mount
  const loadCurrentFilters = useCallback(() => {
    if (typeof window !== 'undefined') {
      try {
        return JSON.parse(localStorage.getItem(CURRENT_KEY) || 'null');
      } catch { }
    }
    return null;
  }, [CURRENT_KEY]);

  // Save current filters
  const saveCurrentFilters = useCallback((filters) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(CURRENT_KEY, JSON.stringify(filters));
    }
  }, [CURRENT_KEY]);

  // Save a named filter preset
  const saveFilterPreset = useCallback((name, filters) => {
    const newPreset = { id: Date.now().toString(), name, filters, createdAt: new Date().toISOString() };
    const updated = [...savedFilters.filter(f => f.name !== name), newPreset];
    setSavedFilters(updated);
    localStorage.setItem(SAVED_KEY, JSON.stringify(updated));
    return newPreset;
  }, [savedFilters, SAVED_KEY]);

  // Delete a saved filter
  const deleteFilterPreset = useCallback((id) => {
    const updated = savedFilters.filter(f => f.id !== id);
    setSavedFilters(updated);
    localStorage.setItem(SAVED_KEY, JSON.stringify(updated));
  }, [savedFilters, SAVED_KEY]);

  // Clear current filters from storage
  const clearCurrentFilters = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(CURRENT_KEY);
    }
  }, [CURRENT_KEY]);

  return {
    savedFilters,
    loadCurrentFilters,
    saveCurrentFilters,
    saveFilterPreset,
    deleteFilterPreset,
    clearCurrentFilters,
  };
}

// ==================== useTableSelection ====================
// Bulk selection management
export function useTableSelection(items = []) {
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === items.length && items.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(item => item.id)));
    }
  }, [items, selectedIds.size]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(items.map(item => item.id)));
  }, [items]);

  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);
  const isAllSelected = items.length > 0 && selectedIds.size === items.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < items.length;

  // Get selected items
  const selectedItems = useMemo(() => 
    items.filter(item => selectedIds.has(item.id)),
    [items, selectedIds]
  );

  return {
    selectedIds,
    selectedItems,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    selectAll,
    isSelected,
    isAllSelected,
    isSomeSelected,
    selectedCount: selectedIds.size,
  };
}

// ==================== useFilterChips ====================
// Quick filter chip management
export function useFilterChips(chipConfigs, currentFilters, onFilterChange) {
  const activeChips = useMemo(() => {
    return chipConfigs.filter(chip => {
      if (chip.isActive) {
        return chip.isActive(currentFilters);
      }
      // Default: check if filter value matches
      return currentFilters[chip.filterKey] === chip.filterValue;
    });
  }, [chipConfigs, currentFilters]);

  const toggleChip = useCallback((chip) => {
    const isActive = activeChips.some(c => c.key === chip.key);
    if (isActive) {
      // Remove the filter
      if (chip.onDeactivate) {
        chip.onDeactivate(onFilterChange);
      } else {
        onFilterChange(chip.filterKey, chip.defaultValue || 'all');
      }
    } else {
      // Apply the filter
      if (chip.onActivate) {
        chip.onActivate(onFilterChange);
      } else {
        onFilterChange(chip.filterKey, chip.filterValue);
      }
    }
  }, [activeChips, onFilterChange]);

  return {
    activeChips,
    toggleChip,
  };
}

// ==================== useColumnVisibility ====================
// Column visibility with locked columns and localStorage persistence
export function useColumnVisibility(storageKey, columns, lockedColumns = []) {
  const STORAGE_KEY = `${storageKey}_columns`;
  
  // Get default visibility (all visible)
  const getDefaultVisibility = useCallback(() => {
    return columns.reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {});
  }, [columns]);

  const [visibility, setVisibility] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (saved) {
          // Ensure locked columns are always visible
          lockedColumns.forEach(key => {
            saved[key] = true;
          });
          return saved;
        }
      } catch { }
    }
    return getDefaultVisibility();
  });

  // Persist to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibility));
    }
  }, [STORAGE_KEY, visibility]);

  const toggleColumn = useCallback((key) => {
    // Don't allow toggling locked columns
    if (lockedColumns.includes(key)) return;
    
    setVisibility(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, [lockedColumns]);

  const showColumn = useCallback((key) => {
    setVisibility(prev => ({ ...prev, [key]: true }));
  }, []);

  const hideColumn = useCallback((key) => {
    if (lockedColumns.includes(key)) return;
    setVisibility(prev => ({ ...prev, [key]: false }));
  }, [lockedColumns]);

  const resetToDefaults = useCallback(() => {
    setVisibility(getDefaultVisibility());
  }, [getDefaultVisibility]);

  const isVisible = useCallback((key) => visibility[key] !== false, [visibility]);
  const isLocked = useCallback((key) => lockedColumns.includes(key), [lockedColumns]);

  // Get visible columns in order
  const visibleColumns = useMemo(() => 
    columns.filter(col => visibility[col.key] !== false),
    [columns, visibility]
  );

  return {
    visibility,
    visibleColumns,
    toggleColumn,
    showColumn,
    hideColumn,
    resetToDefaults,
    isVisible,
    isLocked,
  };
}

// ==================== useTableDensity ====================
// Table density with localStorage persistence
export function useTableDensity(storageKey, defaultDensity = 'comfortable') {
  const STORAGE_KEY = `${storageKey}_density`;
  
  const [density, setDensity] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        return localStorage.getItem(STORAGE_KEY) || defaultDensity;
      } catch { }
    }
    return defaultDensity;
  });

  // Persist to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, density);
    }
  }, [STORAGE_KEY, density]);

  const toggleDensity = useCallback(() => {
    setDensity(d => d === 'comfortable' ? 'compact' : 'comfortable');
  }, []);

  const setComfortable = useCallback(() => setDensity('comfortable'), []);
  const setCompact = useCallback(() => setDensity('compact'), []);

  const isCompact = density === 'compact';
  const isComfortable = density === 'comfortable';

  // CSS classes for density
  const densityClasses = useMemo(() => ({
    row: isCompact ? 'py-1.5' : 'py-3',
    cell: isCompact ? 'px-3 py-1.5 text-xs' : 'px-4 py-3 text-sm',
    header: isCompact ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm',
  }), [isCompact]);

  return {
    density,
    setDensity,
    toggleDensity,
    setComfortable,
    setCompact,
    isCompact,
    isComfortable,
    densityClasses,
  };
}

// ==================== useTableSettings ====================
// Combined hook for column visibility + density
export function useTableSettings(storageKey, columns, lockedColumns = []) {
  const columnVisibility = useColumnVisibility(storageKey, columns, lockedColumns);
  const density = useTableDensity(storageKey);

  const resetAll = useCallback(() => {
    columnVisibility.resetToDefaults();
    density.setComfortable();
  }, [columnVisibility, density]);

  return {
    ...columnVisibility,
    ...density,
    resetAll,
  };
}

